import { useParams, Link as RouterLink } from 'react-router-dom';

import useGoalsMonitoring from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/use-goals-monitoring';
import CriticalElementChart from 'containers/user/goals/goals-monitoring/monitoring-chart/critical-element-chart';
import { MONITORING_CHART_MAX_WIDTH } from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/constants';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Heading from 'components/partials/heading/heading';
import Loader from 'components/partials/loader/loader';

import Icons from 'assets/icons';

interface MetaDataArgs {
  label: string;
  value?: string | number | null;
}

const renderMetaData = ({
  label,
  value = 'N/A',
}: MetaDataArgs): React.JSX.Element => (
  <div className="flex items-center gap-x-2">
    <span className="text-sm text-gray-600">{`${label}:`}</span>
    <span className="text-sm text-gray-600 font-semibold">{value}</span>
  </div>
);

const GoalsMonitoring = (): React.JSX.Element => {
  const { goalId } = useParams<{ goalId: string }>();

  const { loading, monitoringSettings, displayMetrics, goalData } =
    useGoalsMonitoring(goalId);

  return (
    <ResponsiveWrapper customClass="h-full">
      <Loader loading={loading} fullScreen={false} />
      <RouterLink
        to={`/goals`}
        className="flex font-semibold items-center gap-x-1 text-blue-700 hover:text-navy-800 group mt-7 mb-8"
      >
        <Icons.Arrow
          customClass="h-4 w-4 rotate-180 transition-transform duration-300 group-hover:-translate-x-1"
          aria-hidden="true"
        />
        <span>Back to goals</span>
      </RouterLink>
      <Heading variant="h1" customClass="mb-2 block text-2xl">
        {goalData?.summary || `Goal ${goalId}`}
      </Heading>

      <div className="flex items-center gap-x-4 mb-5">
        {renderMetaData({
          label: 'Cohort',
          value: monitoringSettings?.cohort,
        })}
        {renderMetaData({
          label: 'Entry term',
          value: goalData?.enteringClassYear,
        })}
      </div>
      {!!displayMetrics.length && (
        <div className="flex flex-wrap gap-8 items-end">
          {displayMetrics.map((ce) => (
            <div
              key={ce.criticalElementId}
              style={{ width: MONITORING_CHART_MAX_WIDTH, maxWidth: '100%' }}
            >
              <div className="font-bold text-gray-700 mb-3">
                {ce.criticalElementSummary}
              </div>
              <CriticalElementChart
                blocks={ce.blocks}
                previousYear={
                  monitoringSettings?.comparisonYears?.[0] ?? 'Prev'
                }
                currentYear={monitoringSettings?.comparisonYears?.[1] ?? 'Curr'}
                cohortName={monitoringSettings?.cohort}
                isRestricted={ce.isRestricted}
              />
            </div>
          ))}
        </div>
      )}
    </ResponsiveWrapper>
  );
};

export default GoalsMonitoring;
